/* 40b — BG Product block controls (sections/bg-product-block.liquid).
   The track is a CSS scroll-snap row; this adds Ella's prev/next (one page per click, wrapping at the
   ends like slick's infinite mode), page dots, the "n / total" counter and the optional start on
   product 2 from 768px (Ella's slick initialSlide). Per-view counts come from CSS, so nothing here
   knows about breakpoints. No dependencies. */
if (!customElements.get('bg-product-block')) {
  class BgProductBlock extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      if (!this.track) return;
      this.slides = Array.from(this.track.children);
      this.dotsEl = this.querySelector('[data-dots]');
      this.current = this.querySelector('[data-counter-current]');
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.querySelectorAll('button[name="previous"]').forEach((b) => b.addEventListener('click', () => this.page(-1)));
      this.querySelectorAll('button[name="next"]').forEach((b) => b.addEventListener('click', () => this.page(1)));
      this.querySelectorAll('.bg-pb__arrow').forEach((b) => b.removeAttribute('hidden'));

      this.onScroll = () => {
        if (this.raf) return;
        this.raf = requestAnimationFrame(() => {
          this.raf = null;
          this.update();
        });
      };
      this.track.addEventListener('scroll', this.onScroll, { passive: true });

      this.perView = 0;
      this.resizeObserver = new ResizeObserver(() => this.layout());
      this.resizeObserver.observe(this.track);
      this.layout();

      if (this.hasAttribute('data-start-second') && window.matchMedia('(min-width: 768px)').matches) {
        const pv = this.getPerView();
        if (this.slides.length > pv && this.slides[1]) {
          this.track.style.scrollBehavior = 'auto';
          this.track.scrollLeft = this.step();
          this.track.style.scrollBehavior = '';
          this.update();
        }
      }
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.track) this.track.removeEventListener('scroll', this.onScroll);
    }

    step() {
      if (this.slides.length < 2) return this.track.clientWidth;
      return this.slides[1].offsetLeft - this.slides[0].offsetLeft;
    }

    getPerView() {
      const step = this.step();
      return step > 0 ? Math.max(1, Math.round(this.track.clientWidth / step)) : 1;
    }

    index() {
      const step = this.step();
      return step > 0 ? Math.round(this.track.scrollLeft / step) : 0;
    }

    maxIndex() {
      return Math.max(0, this.slides.length - this.getPerView());
    }

    layout() {
      const pv = this.getPerView();
      this.toggleAttribute('data-static', this.slides.length <= pv);
      if (pv !== this.perView) {
        this.perView = pv;
        this.buildDots();
      }
      this.update();
    }

    buildDots() {
      if (!this.dotsEl) return;
      const pages = Math.ceil(this.slides.length / this.perView);
      const label = this.dotsEl.dataset.dotLabel || '';
      const of = this.dotsEl.dataset.ofLabel || '/';
      this.dotsEl.replaceChildren();
      for (let i = 0; i < pages; i += 1) {
        const li = document.createElement('li');
        const button = document.createElement('button');
        button.type = 'button';
        button.setAttribute('aria-label', `${label} ${i + 1} ${of} ${pages}`.trim());
        button.setAttribute('aria-controls', this.track.id);
        button.addEventListener('click', () => this.go(Math.min(i * this.perView, this.maxIndex())));
        li.appendChild(button);
        this.dotsEl.appendChild(li);
      }
      this.dotsEl.toggleAttribute('hidden', pages < 2);
    }

    go(i) {
      this.track.scrollTo({ left: i * this.step(), behavior: this.reduced.matches ? 'auto' : 'smooth' });
    }

    page(dir) {
      const pv = this.getPerView();
      const max = this.maxIndex();
      const i = this.index();
      let target;
      if (dir > 0) target = i >= max ? 0 : Math.min(i + pv, max);
      else target = i <= 0 ? max : Math.max(i - pv, 0);
      this.go(target);
    }

    update() {
      const i = this.index();
      if (this.current) this.current.textContent = String(Math.min(i + 1, this.slides.length));
      if (this.dotsEl && this.perView) {
        const dots = this.dotsEl.querySelectorAll('button');
        const active = i >= this.maxIndex() ? dots.length - 1 : Math.round(i / this.perView);
        dots.forEach((d, n) => d.setAttribute('aria-current', n === active ? 'true' : 'false'));
      }
    }
  }

  customElements.define('bg-product-block', BgProductBlock);
}
